import axios from 'axios'
import { AlertCircle, CheckCircle, Server, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface Config {
  configured: boolean
  endpoint: string | null
  model: string | null
}

export default function LLMOpenClawLocal() {
  const [endpoint, setEndpoint] = useState('http://localhost:1234/v1')
  const [model, setModel] = useState('llama-3.3-70b')
  const [apiKey, setApiKey] = useState('')
  const [config, setConfig] = useState<Config | null>(null)
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Inference state
  const [prompt, setPrompt] = useState('')
  const [maxTokens, setMaxTokens] = useState(1000)
  const [temperature, setTemperature] = useState(0.7)
  const [response, setResponse] = useState('')
  const [inferenceLoading, setInferenceLoading] = useState(false)

  useEffect(() => {
    void loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/api/llm/config/openclaw-local`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setConfig(res.data as Config)

      if (res.data.configured) {
        setEndpoint((res.data.endpoint as string) || '')
        setModel((res.data.model as string) || '')
      }
    } catch (err: any) {
      console.error('Config load failed:', err)
    }
  }

  const handleConfigure = async () => {
    if (!endpoint.trim() || !model.trim()) {
      setError('Endpoint ve model gerekli')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const token = localStorage.getItem('token')
      await axios.post(
        `${API_URL}/api/llm/config/openclaw-local`,
        {
          endpoint: endpoint.trim(),
          model: model.trim(),
          api_key: apiKey.trim() || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      setSuccess('OpenClaw local LLM yapılandırıldı!')
      await loadConfig()
    } catch (err: any) {
      setError((err.response?.data?.detail as string) || 'Yapılandırma başarısız')
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    setError('')
    setSuccess('')

    try {
      const token = localStorage.getItem('token')
      const res = await axios.post(
        `${API_URL}/api/llm/config/openclaw-local/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      if (res.data.status === 'success') {
        setSuccess(res.data.message as string)
      } else {
        setError(res.data.message as string)
      }
    } catch (err: any) {
      setError((err.response?.data?.detail as string) || 'Test başarısız')
    } finally {
      setTesting(false)
    }
  }

  const handleDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('OpenClaw local LLM yapılandırmasını silmek istediğinize emin misiniz?')) {
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/llm/config/openclaw-local`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Yapılandırma silindi')
      setEndpoint('http://localhost:1234/v1')
      setModel('llama-3.3-70b')
      setApiKey('')
      await loadConfig()
    } catch (err: any) {
      setError((err.response?.data?.detail as string) || 'Silme başarısız')
    } finally {
      setLoading(false)
    }
  }

  const handleInference = async () => {
    if (!prompt.trim()) {
      setError('Prompt gerekli')
      return
    }

    if (!config?.configured) {
      setError('Önce OpenClaw local LLM yapılandırın')
      return
    }

    setInferenceLoading(true)
    setError('')
    setResponse('')

    try {
      const token = localStorage.getItem('token')
      const res = await axios.post(
        `${API_URL}/api/llm/inference/openclaw-local`,
        {
          prompt,
          max_tokens: maxTokens,
          temperature
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      setResponse(res.data.response as string)
    } catch (err: any) {
      setError((err.response?.data?.detail as string) || 'Inference başarısız')
    } finally {
      setInferenceLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">OpenClaw Local LLM</h1>
        <p className="text-gray-400">
          Kendi local LLM&apos;inizi kullanın (LM Studio, Ollama) - Maliyet: $0
        </p>
      </div>

      {/* Status Card */}
      {config && (
        <div className={`mb-8 p-4 rounded-lg border ${
          config.configured
            ? 'bg-green-900/20 border-green-500'
            : 'bg-gray-800 border-gray-600'
        }`}>
          <div className="flex items-center gap-2">
            {config.configured ? (
              <>
                <CheckCircle size={20} className="text-green-400" />
                <span className="font-medium">Yapılandırılmış</span>
              </>
            ) : (
              <>
                <Server size={20} className="text-gray-400" />
                <span className="font-medium">Yapılandırılmamış</span>
              </>
            )}
          </div>
          {config.configured && (
            <div className="mt-2 text-sm text-gray-400">
              <div>Endpoint: {config.endpoint}</div>
              <div>Model: {config.model}</div>
            </div>
          )}
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-gray-800 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-bold mb-4">Yapılandırma</h2>

        {/* Endpoint */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Endpoint
            <span className="text-gray-500 ml-2">(LM Studio: http://localhost:1234/v1, Ollama: http://localhost:11434)</span>
          </label>
          <input
            type="text"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="http://localhost:1234/v1"
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        {/* Model */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Model</label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="llama-3.3-70b"
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        {/* API Key (Optional) */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            API Key (Opsiyonel)
            <span className="text-gray-500 ml-2">(Bazı local server&apos;lar gerektirir)</span>
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk-..."
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        {/* Error/Success */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded flex items-start gap-2">
            <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-900/20 border border-green-500 rounded flex items-start gap-2">
            <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
            <span className="text-green-400 text-sm">{success}</span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => void handleConfigure()}
            disabled={loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded transition-colors"
          >
            {loading ? 'Yapılandırılıyor...' : 'Yapılandır'}
          </button>

          {config?.configured && (
            <>
              <button
                onClick={() => void handleTest()}
                disabled={testing}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded transition-colors"
              >
                {testing ? 'Test Ediliyor...' : 'Test Et'}
              </button>

              <button
                onClick={() => void handleDelete()}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded transition-colors"
              >
                Sil
              </button>
            </>
          )}
        </div>
      </div>

      {/* Inference Form */}
      {config?.configured && (
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">LLM Inference</h2>

          {/* Prompt */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Mesajınızı yazın..."
              rows={4}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>

          {/* Advanced Settings */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Max Tokens: {maxTokens}
              </label>
              <input
                type="range"
                min="100"
                max="4000"
                step="100"
                value={maxTokens}
                onChange={(e) => setMaxTokens(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Temperature: {temperature}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={() => void handleInference()}
            disabled={inferenceLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded transition-colors flex items-center justify-center gap-2"
          >
            <Zap size={20} />
            {inferenceLoading ? 'İşleniyor...' : 'Gönder'}
          </button>
        </div>
      )}

      {/* Response */}
      {response && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Yanıt</h2>
          <div className="bg-gray-900 rounded p-4 whitespace-pre-wrap">
            {response}
          </div>
        </div>
      )}
    </div>
  )
}
